/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Offline Indicator Component
 * Monitors network connectivity and displays warning when offline
 *
 * Features:
 * - Online/offline status detection via centralized NetworkStatusService
 * - Auto-dismiss notification when back online
 * - A-2 / W-3 (S711 review): tells the app when the connection returns, and the
 *   Retry button asks every screen to recover -- so both do what they say
 *
 * W-3 (S711 review): the queue-and-replay half of this component was deleted --
 * the "Reconnecting..." branch, the pending-request count, the progress bar and
 * the Clear control. It was written against a queue the shop does not have,
 * nothing fed it, and unreachable code that looks like a feature was being read
 * as evidence that reconnection was handled. Nothing was handled.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { WifiOff, RefreshCw, X, Wifi } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { networkStatus } from "@/lib/network-status";
import i18n from "@/i18n";

/** SU-5 (FCR S732): how long "Still offline" stays after a Retry that found no connection. */
export const STILL_OFFLINE_NOTICE_MS = 3000;

interface OfflineIndicatorProps {
  /** Show a toast notification when coming back online */
  showOnlineToast?: boolean;
  /** Custom message when offline */
  offlineMessage?: string;
  /** Position of the indicator */
  position?: "top" | "bottom";
  /** Callback when online status changes */
  onStatusChange?: (isOnline: boolean) => void;
  /**
   * A-2 (S711 review): called when Retry finds the connection is back, so the
   * button recovers the page as well as re-checking the network.
   */
  onRetry?: () => void;
}

export function OfflineIndicator({
  showOnlineToast = true,
  // S657: main.tsx renders this component with no message, so the English
  // default was the line every non-English customer read when the network
  // dropped. Resolved per render rather than at module load so a language
  // chosen after this module is imported still wins.
  offlineMessage,
  position = "bottom",
  onStatusChange,
  onRetry,
}: OfflineIndicatorProps) {
  const [isOnline, setIsOnline] = useState(networkStatus.isOnline());
  const [isDismissed, setIsDismissed] = useState(false);
  const [showOnlineNotification, setShowOnlineNotification] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const prevOnlineRef = useRef(isOnline);
  const onlineToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribe to centralized network status service
  useEffect(() => {
    const unsubscribe = networkStatus.subscribe((status) => {
      const wasOnline = prevOnlineRef.current;
      const nowOnline = status.isOnline;

      setIsOnline(nowOnline);
      prevOnlineRef.current = nowOnline;

      // Went from offline to online
      if (!wasOnline && nowOnline) {
        setIsDismissed(false);
        if (showOnlineToast) {
          setShowOnlineNotification(true);
          // Replace any in-flight auto-hide timer so rapid online/offline
          // flapping cannot stack timers, and so the timer can be cleared on
          // unmount (see cleanup below).
          if (onlineToastTimerRef.current) {
            clearTimeout(onlineToastTimerRef.current);
          }
          onlineToastTimerRef.current = setTimeout(
            () => setShowOnlineNotification(false),
            3000,
          );
        }
        onStatusChange?.(true);
      }

      // Went from online to offline
      if (wasOnline && !nowOnline) {
        setIsDismissed(false);
        onStatusChange?.(false);
      }
    });

    return () => {
      unsubscribe();
      if (onlineToastTimerRef.current) {
        clearTimeout(onlineToastTimerRef.current);
      }
    };
  }, [showOnlineToast, onStatusChange]);

  // SU-5 (FCR S732): Retry's result is visible. When the connection is still
  // down the button used to spin and stop with nothing said, which read as
  // "Retry does nothing". Now it says so for a few seconds.
  const [stillOffline, setStillOffline] = useState(false);
  const stillOfflineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (stillOfflineTimerRef.current) clearTimeout(stillOfflineTimerRef.current);
    },
    [],
  );

  // Retry: re-check the connection, and when it is there, recover the page.
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      const online = await networkStatus.checkConnectivity();
      if (online) {
        onRetry?.();
      } else {
        setStillOffline(true);
        if (stillOfflineTimerRef.current) clearTimeout(stillOfflineTimerRef.current);
        stillOfflineTimerRef.current = setTimeout(
          () => setStillOffline(false),
          STILL_OFFLINE_NOTICE_MS,
        );
      }
    } finally {
      setIsRetrying(false);
    }
  }, [onRetry]);

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  const positionClasses =
    position === "top"
      ? "fixed top-0 left-0 right-0 z-50"
      : "fixed bottom-0 left-0 right-0 z-50";
  // ST-2 (Session 752): the "back online" notice enters from the edge it sits
  // on -- down from the top edge, up from the bottom edge.
  const enterAnimation =
    position === "top" ? "animate-slide-down" : "animate-slide-up";

  // Don't render if online and no notification to show
  if (isOnline && !showOnlineNotification) {
    return null;
  }

  // Don't render offline banner if dismissed
  if (!isOnline && isDismissed) {
    return null;
  }

  // Online notification
  if (isOnline && showOnlineNotification) {
    return (
      <div className={`${positionClasses} p-4 print:hidden`}>
        <Alert className={`max-w-md mx-auto bg-green-50 border-green-200 shadow-lg ${enterAnimation}`}>
          <Wifi className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">{i18n.t("common.backOnline")}</AlertTitle>
          <AlertDescription className="text-green-700">{i18n.t("common.backOnlineBody")}</AlertDescription>
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-6 w-6 p-0 text-green-600 hover:text-green-800 hover:bg-green-100"
            onClick={() => setShowOnlineNotification(false)}
            aria-label={i18n.t("common.network.dismiss")}
          >
            <X className="h-4 w-4" />
          </Button>
        </Alert>
      </div>
    );
  }

  // Offline indicator
  return (
    <div className={`${positionClasses} p-4 print:hidden`}>
      <Alert className="max-w-lg mx-auto bg-amber-50 border-amber-300 shadow-lg">
        <WifiOff className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800 font-semibold">{i18n.t("common.noInternet")}</AlertTitle>
        <AlertDescription className="text-amber-700 mt-1">
          <p>{offlineMessage ?? i18n.t("common.offlineBody")}</p>
          {stillOffline && (
            <p className="mt-2 font-medium" role="status" data-testid="offline-still-offline">
              {i18n.t("common.network.stillOffline")}
            </p>
          )}

          <div className="flex items-center gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRetry}
              disabled={isRetrying}
              className="border-amber-400 text-amber-800 hover:bg-amber-100"
            >
              {/* W-2 (S711 review): the banner's own words. The spinner label
                  used to be borrowed from a dashboard licence-activation key,
                  so rewording that button would silently reword this one. */}
              <RefreshCw
                className={`h-3 w-3 mr-1${isRetrying ? " animate-spin" : ""}`}
                aria-hidden="true"
              />
              {isRetrying
                ? i18n.t("common.network.checking")
                : i18n.t("common.network.retry")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-amber-700 hover:bg-amber-100"
            >
              {i18n.t("common.network.dismiss")}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default OfflineIndicator;
